import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Hero.css';

// How long each example stays before the next one rolls in
const EXAMPLE_DURATION_MS = 2500;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * True when the visitor has asked their system to reduce motion.
 * @returns {boolean} Whether to avoid animation
 */
function usePrefersReducedMotion() {
  const [prefersReduced, setPrefersReduced] = useState(
    () => window.matchMedia(REDUCED_MOTION_QUERY).matches
  );

  useEffect(() => {
    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    const handleChange = () => setPrefersReduced(query.matches);
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  return prefersReduced;
}

/**
 * CSS state class for an example: shown, on its way out, or waiting below.
 * @param {boolean} isActive - Currently shown
 * @param {boolean} isLeaving - Rolling out upwards
 * @returns {string} Class suffix
 */
function rollState(isActive, isLeaving) {
  if (isActive) return ' is-active';
  if (isLeaving) return ' is-leaving';
  return '';
}

/**
 * Top of the front page: a headline that stands still (`hero.lines`), then a lead-in
 * (`hero.leadIn`) followed by concrete money leaks (`hero.examples`) that roll one by one and
 * loop. Only the examples move. What Revetall does, and the "Prøv gratis" button, follow in the
 * fox section below.
 * With reduced motion the first example is shown, standing still.
 * @returns {JSX.Element} Hero section
 */
function Hero() {
  const { t } = useTranslation();
  const lines = t('hero.lines', { returnObjects: true });
  const examples = t('hero.examples', { returnObjects: true });
  const prefersReducedMotion = usePrefersReducedMotion();
  const [exampleIndex, setExampleIndex] = useState(0);
  const [previousExampleIndex, setPreviousExampleIndex] = useState(null);

  useEffect(() => {
    if (prefersReducedMotion) return undefined;

    const timer = setTimeout(() => {
      setPreviousExampleIndex(exampleIndex);
      setExampleIndex((exampleIndex + 1) % examples.length);
    }, EXAMPLE_DURATION_MS);

    return () => clearTimeout(timer);
  }, [exampleIndex, prefersReducedMotion, examples.length]);

  const shownIndex = prefersReducedMotion ? 0 : exampleIndex;
  const leavingIndex = prefersReducedMotion ? null : previousExampleIndex;

  return (
    <section className="hero">
      <div className="hero-content">
        <h1 className="hero-headline">
          {lines.map((line) => (
            <span key={line} className="hero-headline-line">
              {line}
            </span>
          ))}
        </h1>

        <p className="hero-slips">
          <span className="hero-slips-label">{t('hero.leadIn')}</span>
          {/* Screen readers and search engines get every example once */}
          <span className="visually-hidden">{examples.join(' ')}</span>
          <span className="hero-slips-rotator" aria-hidden="true">
            {examples.map((example, index) => (
              <span
                key={example}
                className={`hero-slips-item${rollState(
                  index === shownIndex,
                  index === leavingIndex
                )}`}
              >
                {example}
              </span>
            ))}
          </span>
        </p>
      </div>
    </section>
  );
}

export default Hero;
